/**
 * Scene Rules Engine
 *
 * The brain of the auto scene generator.
 * Given a Story + ShowTemplate, produces a Scene[] that:
 *   - Matches available assets to appropriate layouts
 *   - Inserts special scenes (L7 for numbers, L8 for quotes, L12 for highlights)
 *   - Varies layouts to avoid visual monotony
 *   - Is deterministic (same story → same scenes every time)
 *
 * Architecture principle:
 *   This engine is PURE — no side effects, no API calls.
 *   Input → Output. Easy to test, easy to extend.
 */
import type { Story, Scene, SceneProps, SceneLayoutId, ShowTemplate, LayoutConfig } from '../types';
import type { VariationState } from './layoutVariationEngine';
import { layoutConfigToSceneOverrides } from './layoutConfigStore';
import { pickLayoutSequence, type LayoutSlot } from './layoutVariationEngine';
import { strToSeed, createRng } from './seededRandom';
import { generateCounterText } from './storyCounterGenerator';
import type { UploadedAsset } from '../../types';
const uuidv4 = () => crypto.randomUUID();

// ─── Asset Profile ────────────────────────────────────────────

interface AssetProfile {
  imageCount: number;
  videoCount: number;
  hasVideo: boolean;
  hasPrimaryImage: boolean;
  hasSecondaryImage: boolean;
  hasPersonPhoto: boolean;
  hasBigNumber: boolean;
  hasQuote: boolean;
  hasHighlightWords: boolean;
  primaryImage?: UploadedAsset;
  secondaryImage?: UploadedAsset;
  allImages: UploadedAsset[];
  primaryVideo?: UploadedAsset;
  personPhoto?: UploadedAsset;
}

function analyzeAssets(story: Story): AssetProfile {
  const images = story.assets.filter((a) => a.role === 'primary' || a.role === 'grid').map((a) => a.asset);
  const secondary = story.assets.find((a) => a.role === 'secondary')?.asset;
  const video = story.assets.find((a) => a.role === 'video' || (a.role === 'primary' && a.asset.type === 'video'))?.asset;
  const person = story.assets.find((a) => a.role === 'person')?.asset;

  return {
    imageCount: images.length,
    videoCount: story.assets.filter((a) => a.asset.type === 'video').length,
    hasVideo: !!video,
    hasPrimaryImage: images.length > 0,
    hasSecondaryImage: !!secondary || images.length >= 2,
    hasPersonPhoto: !!person,
    hasBigNumber: !!story.bigNumber,
    hasQuote: !!story.quoteText,
    hasHighlightWords: (story.headline.split(/\s+/).length >= 4), // always possible to highlight
    primaryImage: images[0],
    secondaryImage: secondary ?? images[1],
    allImages: images,
    primaryVideo: video,
    personPhoto: person,
  };
}

// ─── Layout Slot Builder ──────────────────────────────────────

/**
 * Decide which layout SLOTS (positions in the scene sequence) to use.
 * Some slots are forced (bigNumber → L7, quote → L8).
 * Others are chosen from candidates by the variation engine.
 */
function buildLayoutSlots(
  profile: AssetProfile,
  sceneCount: number,
  allowedLayouts: SceneLayoutId[],
): LayoutSlot[] {
  const slots: LayoutSlot[] = [];

  // Always start with a strong visual if available
  const openingCandidates: SceneLayoutId[] = profile.hasVideo
    ? ['L1', 'L6']
    : profile.hasPrimaryImage
    ? ['L1', 'L6', 'L2']
    : ['L5', 'L12'];

  slots.push({ candidates: openingCandidates.filter((l) => allowedLayouts.includes(l)) });

  // Fill middle scenes
  for (let i = 1; i < sceneCount; i++) {
    const isLast = i === sceneCount - 1;

    // Special forced scenes interspersed
    if (profile.hasBigNumber && i === Math.floor(sceneCount / 2)) {
      slots.push({ forced: 'L7' });
      continue;
    }

    if (profile.hasQuote && profile.hasPersonPhoto && i === sceneCount - 2) {
      slots.push({ forced: 'L8' });
      continue;
    }

    // Build candidate pool based on available assets
    const candidates: SceneLayoutId[] = [];

    if (profile.hasPrimaryImage) {
      candidates.push('L1', 'L6');
      if (!isLast) candidates.push('L5');
    }

    if (profile.hasSecondaryImage) {
      candidates.push('L2', 'L3', 'L4');
    }

    if (profile.imageCount >= 3) {
      candidates.push('L10');
    }

    if (isLast) {
      // Closing scene: strong visual or headline emphasis
      candidates.push('L5', 'L12');
    }

    if (!profile.hasPrimaryImage) {
      candidates.push('L5', 'L12');
    }

    // Filter to allowed layouts, fallback to L5 if empty
    const filtered = [...new Set(candidates)].filter((l) => allowedLayouts.includes(l));
    slots.push({ candidates: filtered.length > 0 ? filtered : ['L5'] });
  }

  return slots;
}

// ─── Scene Props Builder ──────────────────────────────────────

function buildSceneProps(
  story: Story,
  layoutId: SceneLayoutId,
  profile: AssetProfile,
  showTemplate: ShowTemplate,
  sceneIndex: number,
  bgMusicUrl?: string,
  bgMusicVolume?: number,
  voiceoverVolume?: number,
  layoutConfigs?: LayoutConfig[],
): SceneProps {
  // Apply per-layout config defaults if available
  const layoutConfig = layoutConfigs?.find((c) => c.id === layoutId);
  const configOverrides = layoutConfig ? layoutConfigToSceneOverrides(layoutConfig) : {};
  const counterText = showTemplate.globalOverlay.storyCounter
    ? generateCounterText(story.index, showTemplate.globalOverlay.counterPattern)
    : undefined;

  // Assets for this specific layout
  const assets = buildLayoutAssets(layoutId, profile);

  return {
    headline: story.headline,
    subheadline: story.subheadline || undefined,
    bigNumber: story.bigNumber || undefined,
    quoteText: story.quoteText || undefined,
    reporterName: story.reporterName || undefined,
    location: story.location || undefined,

    layoutId,
    assets,

    colorPalette: showTemplate.colorPalette,
    headlineFont: showTemplate.headlineFont,
    subheadlineFont: showTemplate.subheadlineFont,

    tickerText: story.tickerText || undefined,
    showTicker: showTemplate.globalOverlay.ticker && sceneIndex === 0
      ? true
      : showTemplate.globalOverlay.ticker,
    tickerSpeed: showTemplate.globalOverlay.tickerSpeed,

    showLogo: showTemplate.globalOverlay.logo,
    showTopBar: showTemplate.globalOverlay.topBar && sceneIndex === 0,

    storyCounterText: counterText,
    showStoryCounter: showTemplate.globalOverlay.storyCounter && sceneIndex === 0,

    isBreaking: story.isBreaking,
    breakingLabel: story.isBreaking ? 'BREAKING NEWS' : undefined,

    // Highlight every word that's 4+ chars — simple heuristic
    highlightWords: layoutId === 'L12'
      ? story.headline.split(/\s+/).filter((w) => w.length >= 4).slice(0, 3)
      : undefined,

    // Audio — voiceover plays only on first scene of a story
    voiceoverUrl: sceneIndex === 0
      ? (story.assets.find((a) => a.role === 'audio')?.asset.url)
      : undefined,
    voiceoverVolume: voiceoverVolume ?? 1.0,
    bgMusicUrl,
    bgMusicVolume: bgMusicVolume ?? 0.3,

    // Layout config overrides (user-editable per-layout defaults)
    ...configOverrides,
  };
}

function buildLayoutAssets(layoutId: SceneLayoutId, profile: AssetProfile) {
  switch (layoutId) {
    case 'L4': // Two Visual Split
      return {
        primary: profile.primaryImage ?? profile.primaryVideo,
        secondary: profile.secondaryImage,
      };
    case 'L8': // Quote — needs person photo
      return {
        primary: profile.primaryImage,
        personPhoto: profile.personPhoto ?? profile.primaryImage,
      };
    case 'L10': // Grid — all images
      return {
        primary: profile.primaryImage,
        secondary: profile.secondaryImage,
        images: profile.allImages.slice(2, 4),
      };
    case 'L7': // Big Number — background image optional
      return { primary: profile.primaryImage };
    case 'L12': // Text Highlight — background image optional
      return { primary: profile.primaryImage };
    default:
      return {
        primary: profile.hasVideo && (layoutId === 'L1' || layoutId === 'L11')
          ? profile.primaryVideo
          : profile.primaryImage ?? profile.primaryVideo,
        secondary: profile.secondaryImage,
        video: profile.primaryVideo,
      };
  }
}

// ─── Scene Duration Calculator ────────────────────────────────

function calcSceneDuration(
  layoutId: SceneLayoutId,
  storyDuration: number,
  sceneCount: number,
  defaultSceneDuration: number,
): number {
  // Special scenes get fixed short durations
  if (layoutId === 'L7') return Math.min(defaultSceneDuration * 0.8, 6);
  if (layoutId === 'L8') return defaultSceneDuration * 1.2;
  if (layoutId === 'L5') return Math.min(defaultSceneDuration * 0.7, 5);

  // Distribute remaining time evenly among normal scenes
  return Math.max(4, storyDuration / sceneCount);
}

// ─── Main Entry Point ─────────────────────────────────────────

export interface SceneGenerationInput {
  story: Story;
  showTemplate: ShowTemplate;
  variationState: VariationState;
  bgMusicUrl?: string;
  bgMusicVolume?: number;
  voiceoverVolume?: number;
  layoutConfigs?: LayoutConfig[];
}

export interface SceneGenerationResult {
  scenes: Scene[];
  nextVariationState: VariationState;
}

/**
 * Generate scenes for a single story.
 * Call this for each story in order, passing the returned variationState
 * as input to the next call (maintains anti-repetition state across stories).
 */
export function generateScenesForStory(input: SceneGenerationInput): SceneGenerationResult {
  const { story, showTemplate, variationState, bgMusicUrl, bgMusicVolume, voiceoverVolume, layoutConfigs } = input;
  const profile = analyzeAssets(story);
  const rng = createRng(strToSeed(story.id));

  const sceneCount = showTemplate.rules.scenesPerStory;
  const allowedLayouts = showTemplate.allowedLayouts;

  // Build layout slots
  const slots = buildLayoutSlots(profile, sceneCount, allowedLayouts);

  // Pick layouts using variation engine (deterministic + anti-repetition)
  const { layouts, nextState } = pickLayoutSequence(slots, variationState, showTemplate.rules, rng);

  // Build Scene objects
  let startSec = 0;
  const scenes: Scene[] = layouts.map((layoutId, i) => {
    const duration = calcSceneDuration(
      layoutId,
      story.duration,
      layouts.length,
      showTemplate.rules.defaultSceneDuration,
    );

    const scene: Scene = {
      id: uuidv4(),
      layoutId,
      duration,
      startSec,
      props: buildSceneProps(story, layoutId, profile, showTemplate, i, bgMusicUrl, bgMusicVolume, voiceoverVolume, layoutConfigs),
      transitionIn: i === 0 ? 'cut' : showTemplate.midTransition,
    };

    startSec += duration;
    return scene;
  });

  return { scenes, nextVariationState: nextState };
}

// ─── Batch: generate scenes for all stories ───────────────────

/**
 * Generate scenes for all enabled stories in a show.
 * Returns updated stories with scene[] populated.
 */
export interface GenerateAllScenesOptions {
  bgMusicUrl?: string;
  bgMusicVolume?: number;
  voiceoverVolume?: number;
  initialVariationState?: VariationState;
  layoutConfigs?: LayoutConfig[];
}

export function generateAllScenes(
  stories: Story[],
  showTemplate: ShowTemplate,
  options?: GenerateAllScenesOptions,
): Story[] {
  let variationState: VariationState = options?.initialVariationState ?? {
    recentLayouts: [],
    usageCounts: {},
    consecutiveCount: 0,
    lastLayout: null,
  };

  return stories.map((story) => {
    if (!story.enabled) return story;

    const { scenes, nextVariationState } = generateScenesForStory({
      story,
      showTemplate,
      variationState,
      bgMusicUrl: options?.bgMusicUrl,
      bgMusicVolume: options?.bgMusicVolume,
      voiceoverVolume: options?.voiceoverVolume,
      layoutConfigs: options?.layoutConfigs,
    });

    variationState = nextVariationState;

    return { ...story, scenes };
  });
}
